import { Outlet, useLocation } from "react-router-dom";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import { SignIn } from "src/handlers/Account";
import { useAuthState } from "src/handlers/Account/useAuthState";

/**
 * Wraps routes that require an authenticated user. When signed out, renders
 * a prompt that triggers the sign-in flow instead of the protected page.
 *
 * Usage:
 *   <Route element={<ProtectedRoute />}>
 *     <Route path="deployment" element={<Deployment />} />
 *   </Route>
 */
function ProtectedRoute({ children, redirectTo }) {
  const { isSignedIn } = useAuthState();
  const location = useLocation();

  if (isSignedIn) {
    return children || <Outlet />;
  }

  const handleSignIn = () => {
    const target = redirectTo || `${location.pathname}${location.search || ""}`;
    SignIn(target);
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
      <Stack spacing={3} alignItems="center" sx={{ textAlign: "center" }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: (theme) =>
              theme.palette.mode === "light" ? "rgba(59,130,246,0.12)" : "rgba(56,189,248,0.14)",
            color: (theme) => theme.palette.primary.main
          }}
        >
          <LockOutlinedIcon fontSize="large" />
        </Box>
        <Stack spacing={1}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Sign in required
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You need to be signed in to view this page.
          </Typography>
        </Stack>
        <Button variant="contained" size="large" onClick={handleSignIn}>
          Sign in
        </Button>
      </Stack>
    </Container>
  );
}

export default ProtectedRoute;
